// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { decodeBase64UrlUtf8, refreshAccessToken, TokenRefreshError } from '@/auth/oidc';

export type UserSession = { id: string; forename: string; token: string; idToken?: string | null; refreshToken?: string | null };

type UserContextValue = {
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
  clear: () => void;
  /**
   * False on native until the stored session has been read back from AsyncStorage.
   * Callers that redirect unauthenticated users must wait for this, or a cold start
   * bounces a logged-in user to Keycloak before their session has loaded.
   */
  sessionHydrated: boolean;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

const STORAGE_KEY = 'goffy:user';
const EXP_SKEW_SECONDS = 30;
const REFRESH_LEAD_SECONDS = 60;
const REFRESH_RETRY_COOLDOWN_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 1_000;
// A refresh that fails for transport reasons is retried on this ladder rather than ending the
// session. The access token lives 24h, so the refresh is a once-a-day single shot: without a
// retry, one bad moment on a course with no signal is a trip back to the Keycloak login form.
const TRANSIENT_RETRY_BACKOFF_MS = [5_000, 15_000, 60_000, 300_000];

// Shares the decoder in auth/oidc.ts rather than repeating it: that one pads the
// base64url before decoding and works whether the runtime offers Buffer or atob.
// A failure here is silent — it makes a token look like it has no exp, which
// disables the refresh timer below — so it is worth not hand-rolling twice.
function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const parsed = JSON.parse(decodeBase64UrlUtf8(payload)) as { exp?: number };
    return typeof parsed.exp === 'number' ? parsed.exp : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const exp = decodeJwtExp(token);
  if (!exp) return false;
  const nowSeconds = Date.now() / 1000;
  return exp <= nowSeconds + EXP_SKEW_SECONDS;
}

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

// Null on native, where React Native defines `window` but no `localStorage`.
const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

function parseStoredUser(raw: string | null): UserSession | null {
  if (!raw) return null;
  const parsed = JSON.parse(raw) as UserSession;
  if (!parsed?.token) return null;
  if (isTokenExpired(parsed.token) && !parsed.refreshToken) return null;
  return parsed;
}

/** Synchronous load, so web restores a session before the first render. */
function loadUser(): UserSession | null {
  try {
    const local = getLocalStorage();
    if (!local) return null;
    const user = parseStoredUser(local.getItem(STORAGE_KEY));
    if (!user) local.removeItem(STORAGE_KEY);
    return user;
  } catch {
    return null;
  }
}

/** Native counterpart; AsyncStorage has no synchronous read. */
async function loadUserAsync(): Promise<UserSession | null> {
  try {
    if (!asyncStorage) return null;
    const user = parseStoredUser(await asyncStorage.getItem(STORAGE_KEY));
    if (!user) await asyncStorage.removeItem(STORAGE_KEY);
    return user;
  } catch {
    return null;
  }
}

function persistUser(user: UserSession | null) {
  try {
    const local = getLocalStorage();
    if (local) {
      if (user) {
        local.setItem(STORAGE_KEY, JSON.stringify(user));
      } else {
        local.removeItem(STORAGE_KEY);
      }
      return;
    }
    if (!asyncStorage) return;
    void (user
      ? asyncStorage.setItem(STORAGE_KEY, JSON.stringify(user))
      : asyncStorage.removeItem(STORAGE_KEY)
    ).catch(() => undefined);
  } catch {
    // ignore storage failures
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<UserSession | null>(() => loadUser());
  // Web reads localStorage synchronously above, so it is hydrated from the start.
  const [sessionHydrated, setSessionHydrated] = useState(() => !!getLocalStorage());
  const refreshInFlight = useRef(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextRefreshAllowedAtRef = useRef(0);
  const transientRetryRef = useRef(0);
  // Set once anything explicitly assigns a session, so a slow AsyncStorage read
  // cannot resurrect a session that was cleared while it was in flight.
  const userAssignedRef = useRef(false);

  // Keycloak rotates the refresh token on every use, so a second tab still holding the previous
  // one would get invalid_grant and clear the shared storage key — logging every tab out. Adopt
  // whatever the tab that refreshed wrote. setUserState, not applyUser: writing back here would
  // echo the value straight into the other tab.
  useEffect(() => {
    // React Native defines `window` but neither localStorage nor addEventListener, so both have
    // to be probed before use — same trap the base URL constants in auth/oidc.ts work around.
    if (typeof window === 'undefined') return;
    if (!getLocalStorage() || typeof window.addEventListener !== 'function') return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      userAssignedRef.current = true;
      if (event.newValue === null) {
        setUserState(null);
        return;
      }
      try {
        const next = parseStoredUser(event.newValue);
        if (next) setUserState(next);
      } catch {
        // A malformed write from another tab is not worth ending this one's session over.
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (sessionHydrated) return;
    let cancelled = false;
    loadUserAsync()
      .then((stored) => {
        if (cancelled || !stored || userAssignedRef.current) return;
        setUserState(stored);
      })
      .finally(() => {
        if (!cancelled) setSessionHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionHydrated]);

  const applyUser = (next: UserSession | null) => {
    userAssignedRef.current = true;
    setUserState(next);
    persistUser(next);
  };

  const refreshSession = useCallback(async (current: UserSession) => {
    if (!current.refreshToken || refreshInFlight.current) return;
    const nowMs = Date.now();
    if (nowMs < nextRefreshAllowedAtRef.current) {
      const retryInMs = Math.max(0, nextRefreshAllowedAtRef.current - nowMs);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      refreshTimeoutRef.current = setTimeout(() => {
        void refreshSession(current);
      }, retryInMs);
      return;
    }
    refreshInFlight.current = true;
    try {
      const refreshed = await refreshAccessToken(current.refreshToken);
      const refreshedExp = decodeJwtExp(refreshed.accessToken);
      const nowSeconds = Date.now() / 1000;
      if (typeof refreshedExp === 'number' && refreshedExp <= nowSeconds + EXP_SKEW_SECONDS) {
        const skewSeconds = Math.ceil((nowSeconds + EXP_SKEW_SECONDS) - refreshedExp);
        nextRefreshAllowedAtRef.current = Date.now() + REFRESH_RETRY_COOLDOWN_MS;
        console.warn('[auth] Ignoring refreshed token that is already expired.', {
          exp: refreshedExp,
          now: Math.floor(nowSeconds),
          skewSeconds,
          retryInMs: REFRESH_RETRY_COOLDOWN_MS,
        });
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
        refreshTimeoutRef.current = setTimeout(() => {
          void refreshSession(current);
        }, REFRESH_RETRY_COOLDOWN_MS);
        return;
      }
      nextRefreshAllowedAtRef.current = 0;
      transientRetryRef.current = 0;
      applyUser({
        ...current,
        token: refreshed.accessToken,
        idToken: refreshed.idToken ?? current.idToken ?? null,
        refreshToken: refreshed.refreshToken ?? current.refreshToken ?? null,
      });
    } catch (err) {
      if (err instanceof TokenRefreshError && err.isSessionEnded) {
        // Another tab may have rotated the token out from under us between our read and our
        // request — each refresh token is good for exactly one use. If storage now holds a
        // different one, the session is alive and that tab won the race, so adopt its token
        // instead of logging the whole browser out.
        const stored = loadUser();
        if (stored?.refreshToken && stored.refreshToken !== current.refreshToken) {
          userAssignedRef.current = true;
          setUserState(stored);
          return;
        }
        applyUser(null);
        return;
      }
      // Transport failure: the refresh token is untouched, so keep the session and try again.
      const attempt = transientRetryRef.current;
      const retryInMs = TRANSIENT_RETRY_BACKOFF_MS[Math.min(attempt, TRANSIENT_RETRY_BACKOFF_MS.length - 1)];
      transientRetryRef.current = attempt + 1;
      console.warn('[auth] Token refresh failed without an answer from Keycloak; keeping the session.', {
        attempt: attempt + 1,
        retryInMs,
      });
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      refreshTimeoutRef.current = setTimeout(() => {
        void refreshSession(current);
      }, retryInMs);
    } finally {
      refreshInFlight.current = false;
    }
  }, []);

  // Auto-clear when token expires to force a fresh login.
  useEffect(() => {
    if (!user) return;
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    const exp = decodeJwtExp(user.token);
    if (!exp) return;
    const nowSeconds = Date.now() / 1000;
    const refreshAtSeconds = exp - REFRESH_LEAD_SECONDS;
    const nowMs = Date.now();
    const earliestAllowedAtMs = Math.max(nowMs + MIN_REFRESH_DELAY_MS, nextRefreshAllowedAtRef.current);
    const requestedRefreshAtMs = refreshAtSeconds * 1000;
    const targetRefreshAtMs = refreshAtSeconds <= nowSeconds + EXP_SKEW_SECONDS
      ? earliestAllowedAtMs
      : Math.max(requestedRefreshAtMs, earliestAllowedAtMs);
    const msUntilRefresh = Math.max(0, targetRefreshAtMs - nowMs);
    refreshTimeoutRef.current = setTimeout(() => {
      void refreshSession(user);
    }, msUntilRefresh);
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [user, refreshSession]);

  const value = useMemo(
    () => ({
      user,
      sessionHydrated,
      setUser: (next: UserSession | null) => {
        if (next?.token && isTokenExpired(next.token) && !next.refreshToken) {
          applyUser(null);
          return;
        }
        applyUser(next);
      },
      clear: () => {
        applyUser(null);
      },
    }),
    [user, sessionHydrated]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within UserProvider');
  }
  return ctx;
}

// Non-visual helper route placeholder for Expo Router.
export default function UserContextRoute() {
  return null;
}
